/**
 * Paleta de colores y configuraciones para estados de tareas
 * Centraliza todos los colores y estilos relacionados con estados de tareas
 * para mantener consistencia en toda la aplicación
 */

// ============================================================================
// COLORES HEXADECIMALES POR ESTADO
// ============================================================================

export const TASK_STATUS_COLORS = {
  pendiente: '#eab308',    // Amarillo
  en_camino: '#3b82f6',    // Azul
  iniciada: '#3b82f6',     // Azul
  relevada: '#656566',     // Gris
  finalizada: '#0bcb38',   // Verde
  cancelada: '#ef4444',    // Rojo
};

// ============================================================================
// ETIQUETAS EN ESPAÑOL
// ============================================================================

export const TASK_STATUS_LABELS = {
  pendiente: 'Pendiente',
  en_camino: 'En Camino',
  iniciada: 'Iniciada',
  relevada: 'Relevada',
  finalizada: 'Finalizada',
  cancelada: 'Cancelada',
};

// ============================================================================
// CONFIGURACIÓN COMPLETA (para compatibilidad con código existente)
// ============================================================================

export const TASK_STATUS_CONFIG = {
  pendiente: { 
    color: TASK_STATUS_COLORS.pendiente, 
    label: TASK_STATUS_LABELS.pendiente 
  },
  en_camino: { 
    color: TASK_STATUS_COLORS.en_camino, 
    label: TASK_STATUS_LABELS.en_camino 
  },
  iniciada: { 
    color: TASK_STATUS_COLORS.iniciada, 
    label: TASK_STATUS_LABELS.iniciada 
  },
  relevada: { 
    color: TASK_STATUS_COLORS.relevada, 
    label: TASK_STATUS_LABELS.relevada 
  },
  finalizada: { 
    color: TASK_STATUS_COLORS.finalizada, 
    label: TASK_STATUS_LABELS.finalizada 
  },
  cancelada: { 
    color: TASK_STATUS_COLORS.cancelada, 
    label: TASK_STATUS_LABELS.cancelada 
  },
};

// ============================================================================
// CLASES DE TAILWIND CSS PARA BADGES Y BACKGROUNDS
// ============================================================================

/**
 * Clases de Tailwind para badges oscuros (para dashboard de técnicos)
 */
export const TASK_STATUS_BADGE_CLASSES = {
  pendiente: 'bg-st-pendiente/12 text-st-pendiente border-st-pendiente/30',
  en_camino: 'bg-st-en-camino/12 text-st-en-camino border-st-en-camino/30',
  llegando: 'bg-st-en-camino/12 text-st-en-camino border-st-en-camino/30',
  iniciada: 'bg-st-en-camino/12 text-st-en-camino border-st-en-camino/30',
  relevada: 'bg-st-relevada/12 text-st-relevada border-st-relevada/30',
  finalizada: 'bg-st-finalizada/12 text-st-finalizada border-st-finalizada/30',
  cancelada: 'bg-st-cancelada/12 text-st-cancelada border-st-cancelada/30',
};

/**
 * Clases de Tailwind para badges claros (para tracking de clientes).
 * Encalle sólo expone el tema oscuro (B1 = A); se reusa el mismo tinte al
 * 12 % que el resto de los badges de estado en vez de los tonos claros de
 * OnPower que este export nunca terminó de consumir ningún componente.
 */
export const TASK_STATUS_BADGE_LIGHT_CLASSES = {
  pendiente: 'bg-st-pendiente/12 text-st-pendiente',
  en_camino: 'bg-st-en-camino/12 text-st-en-camino',
  iniciada: 'bg-st-en-camino/12 text-st-en-camino',
  relevada: 'bg-st-relevada/12 text-st-relevada',
  finalizada: 'bg-st-finalizada/12 text-st-finalizada',
  cancelada: 'bg-st-cancelada/12 text-st-cancelada',
};

// ============================================================================
// FUNCIONES HELPER
// ============================================================================

/**
 * Obtener color hexadecimal por estado
 * @param {string} status - Estado de la tarea
 * @returns {string} Color hexadecimal
 */
export function getTaskStatusColor(status) {
  return TASK_STATUS_COLORS[status] || TASK_STATUS_COLORS.pendiente;
}

/**
 * Obtener etiqueta por estado
 * @param {string} status - Estado de la tarea
 * @returns {string} Etiqueta en español
 */
export function getTaskStatusLabel(status) {
  return TASK_STATUS_LABELS[status] || 'Desconocido';
}

/**
 * Obtener configuración completa por estado
 * @param {string} status - Estado de la tarea
 * @returns {Object} Objeto con color y label
 */
export function getTaskStatusConfig(status) {
  return TASK_STATUS_CONFIG[status] || TASK_STATUS_CONFIG.pendiente;
}

/**
 * Obtener clases de Tailwind para badges oscuros
 * @param {string} status - Estado de la tarea
 * @returns {string} Clases CSS de Tailwind
 */
export function getTaskStatusBadgeClasses(status) {
  return TASK_STATUS_BADGE_CLASSES[status] || TASK_STATUS_BADGE_CLASSES.pendiente;
}

/**
 * Obtener clases de Tailwind para badges claros
 * @param {string} status - Estado de la tarea
 * @returns {string} Clases CSS de Tailwind
 */
export function getTaskStatusBadgeLightClasses(status) {
  return TASK_STATUS_BADGE_LIGHT_CLASSES[status] || TASK_STATUS_BADGE_LIGHT_CLASSES.pendiente;
}

/**
 * Obtener estilos inline para badges usando colores hexadecimales
 * Diseño: fondo transparente con borde y texto de color
 * @param {string} status - Estado de la tarea
 * @returns {Object} Objeto con estilos CSS inline
 */
export function getTaskStatusBadgeStyles(status) {
  const color = TASK_STATUS_COLORS[status] || TASK_STATUS_COLORS.pendiente;
  
  return {
    backgroundColor: 'transparent',
    color: color,
    border: `2px solid ${color}`,
  };
}

/**
 * Lista de todos los estados válidos
 */
export const VALID_TASK_STATUSES = [
  'pendiente',
  'en_camino',
  'iniciada',
  'relevada',
  'finalizada',
  'cancelada',
];

/**
 * Verificar si un estado es válido
 * @param {string} status - Estado a verificar
 * @returns {boolean} true si es válido
 */
export function isValidTaskStatus(status) {
  return VALID_TASK_STATUSES.includes(status);
}
